import { useMemo } from 'react';
import { Category } from '../../../types/index.ts';
import { CATEGORY_COLORS } from '../../../pages/Inventory.tsx';

// ─── Shared category-color resolver ("Categories B", 2026-10-11) ───────────────────────────────
// Moved verbatim (same algorithm, same output) from Inventory.tsx's own categoryColorMap memo --
// sequential color assignment walking the tree in the exact depth-first order the sidebar
// actually renders it (top-level siblings in order, each one's children before its next
// sibling), so adjacent rows never repeat purely by DB-id coincidence (the old getCategoryColor
// id%10 hash could land Equipment/Kitchen both on the same color). Deliberately independent of
// any expanded/collapsed UI state -- a category's positional color stays stable regardless of
// whether its parent happens to be expanded.
export function useCategoryColorMap(categories: Category[]): Map<number, string> {
  return useMemo(() => {
    const map = new Map<number, string>();
    let i = 0;
    function walk(list: Category[]) {
      for (const c of list) {
        map.set(c.id, CATEGORY_COLORS[i % CATEGORY_COLORS.length]);
        i++;
        walk(categories.filter((child) => child.parent_id === c.id));
      }
    }
    walk(categories.filter((c) => !c.parent_id));
    return map;
  }, [categories]);
}

// Neutral fallback when a category id isn't in the positional map at all (shouldn't normally
// happen -- the map covers every category passed in -- but mirrors the same defensive fallback
// CatSidebarItem already had via getCategoryColor()).
const NEUTRAL_COLOR = '#94A3B8';

// resolveCategoryColor: explicit per-category override (color column, Categories B) first, then
// the positional sidebar color, then neutral. No NULL-inherit from a parent at render time --
// a category either has its own saved hex or falls through to its positional slot; "inherit
// parent's color" only ever happens explicitly at creation time (new subcategories are created
// WITH the parent's resolved hex already written in, not left null to inherit implicitly).
export function resolveCategoryColor(
  cat: Pick<Category, 'id' | 'color'> | null | undefined,
  categoryColorMap: Map<number, string>,
): string {
  if (!cat) return NEUTRAL_COLOR;
  if (cat.color) return cat.color;
  return categoryColorMap.get(cat.id) ?? NEUTRAL_COLOR;
}
